import {ConfigOptions, jsx, ParamComponent} from 'panel';

import {FeatureGate, ReportName} from 'common/report/constants';
import {DashboardOrigin} from 'common/report/util/bookmark-overrides';
import {SearchDisplayContext} from 'common/report/util/search/types';
import {QueryBuilderEl} from 'common/query-builder/query-builder';
import {QueryBuilderUpdateDetail, ReportSwitchEvent} from 'common/query-builder/query-builder/types';
import {DataGroup} from 'common/widgets/data-group-selector/types';
import {CustomEventDefinition} from 'common/report/util/saved-entities/types';
import {QueryReason} from 'common/report/util/query-tracking/types';
import {CurrentProject} from 'common/report/model-resources/projects';
import {propagateEvent} from 'common/util/dom';
import {canUseSlowlyChangingDimensions} from 'common/query-builder/query-block/properties-menu/util';

import {BaseQueryErrorType, BaseQueryResultsType} from '../types';
import {FeatureRolloutName, FeatureRollouts} from '../util/feature-sampling';
import {BookmarkType} from '../queries/app-api/bookmark-client';
import {BookmarkUpdateEvent} from './types';

export type ReportQueryBuilderParams<
  BookmarkParamsType,
  QueryResultsType = BaseQueryResultsType,
  ErrorType = BaseQueryErrorType
> = {
  originDashboard: DashboardOrigin;
  projectId: number;
  userId: number;
  workspaceId: number;
  appMode: boolean;
  project: CurrentProject;
  bookmarkParams: BookmarkParamsType;
  results: QueryResultsType;
  error: ErrorType;
  dataGroups?: DataGroup[];
  customEvents?: CustomEventDefinition[];
  insightsReportMode?: BookmarkType;
};

export type ReportQueryBuilderEventHandlers = {
  update: (ev: BookmarkUpdateEvent) => void;
  reportswitch: (ev: ReportSwitchEvent) => void;
};

export abstract class ReportQueryBuilder<
  BookmarkParamsType,
  QueryResultsType = BaseQueryResultsType,
  StateType = EmptyObj
> extends ParamComponent<ReportQueryBuilderParams<BookmarkParamsType, QueryResultsType>, StateType> {
  protected displayContext: SearchDisplayContext;
  protected reportType: ReportName;

  get defaultState(): StateType {
    return {} as StateType;
  }

  abstract bookmarkUpdateFromQbUpdate(qbUpdate: QueryBuilderUpdateDetail): BookmarkParamsType;

  get config(): ConfigOptions<
    StateType,
    unknown,
    unknown,
    ReportQueryBuilderParams<BookmarkParamsType, QueryResultsType>
  > {
    return {
      defaultState: this.defaultState,
      params: {
        originDashboard: Object,
        projectId: Number,
        userId: Number,
        workspaceId: Number,
        appMode: Boolean,

        project: Object,
        // @ts-ignore - generic type doesn't work well with `InferType`, need to find a fix
        bookmarkParams: Object,
        // @ts-ignore - generic type doesn't work well with `InferType`, need to find a fix
        results: Object,
        error: Object,
        dataGroups: Array,
        customEvents: Array,
        insightsReportMode: String,
      },

      hooks: {
        preUpdate: (_state, newParams?: ReportQueryBuilderParams<BookmarkParamsType, QueryResultsType>) => {
          if (!newParams) return;
          if (newParams.bookmarkParams) {
            this.onBookmarkUpdated(newParams.bookmarkParams);
          }
        },
      },
      template: () => {
        return (
          <QueryBuilderEl
            attrs={{
              elref: `commonQueryBuilder`,
            }}
            params={
              !this.params.bookmarkParams || !this.params.project
                ? {
                    projectId: this.params.projectId,
                    workspaceId: this.params.workspaceId,
                  }
                : {
                    projectId: this.params.projectId,
                    reportHeaderOnly: false,
                    switchableReportName: this.reportType,
                    trackName: this.reportType,
                    // to be overbidden by insight report, default synch with trackName
                    // track-name is being used for many things, including controlling which
                    // report-header to show
                    reportName: this.reportType,
                    userId: this.params.userId,
                    workspaceId: this.params.workspaceId,

                    // TODO centralize this logic in the projects Model Context (currently duped across
                    // apps)
                    allDataGroups: this.canUseDataGroups(),
                    dataGroupsEnabled: this.canUseDataGroups(),
                    maxDataHistoryDays: this.params.project.getFeatureGate(FeatureGate.MaxDataHistoryDays),
                    projectAsOfDate: this.params.project.projectAsOfDate,
                    projectHasCohorts: this.canUseCohorts(),
                    projectHasPeople: this.params.project.hasIntegratedEngage,
                    projectIsFreePlan: this.params.project.isFreeFeatureGateTier(),
                    slowlyChangingDimensionsPocEnabled: canUseSlowlyChangingDimensions(this.params.project),

                    restrictTopEventsOnly: false,
                    showSessions: true,
                    showTopEvents: true,
                    countMenu: this.qbCountMenuTemplate(),
                    bodySection: this.qbChildrenTemplate(),

                    ...this.reportSpecificQBParams(),
                  }
            }
            on={this.queryBuilderEventHandlers}
          />
        );
      },
    };
  }

  // to be overridden if extra processing is needed when bookmark gets changed
  // by another component
  protected onBookmarkUpdated(_newBookmarkParams: BookmarkParamsType) {
    this.update();
  }

  // to be overridden by reports that need slotted content
  protected qbChildrenTemplate() {
    return null;
  }

  // for funnels & retention, we have a menu for user to select how they intend to count
  // e.g. unique or total, aggregate by property, etc.
  protected qbCountMenuTemplate() {
    return null;
  }

  canUseBehaviors() {
    return false;
  }

  canUseCohorts() {
    return !!this.params.project?.canUseCohorts();
  }

  canUseDataGroups() {
    return !!this.params.project?.canUseDataGroups();
  }

  hasConsolidateMetricsUI() {
    return FeatureRollouts[FeatureRolloutName.InsightsNewCountMenu].isEnabled(this.params.project);
  }

  dispatchUpdate({
    params,
    queryReason,
    requery = true,
  }: {
    params: BookmarkParamsType;
    queryReason: QueryReason;
    requery?: boolean;
  }) {
    const event: BookmarkUpdateEvent = new CustomEvent(`update`, {detail: {params, requery, queryReason}});
    this.dispatchEvent(event);
  }

  private get queryBuilderEventHandlers() {
    return {
      update: (ev) => {
        const params = this.bookmarkUpdateFromQbUpdate(ev.detail);
        this.dispatchUpdate({params, queryReason: ev.detail.queryReason, requery: ev.detail.requery ?? true});
      },
      reportswitch: (ev) => propagateEvent(this, ev),
    };
  }

  protected reportSpecificQBParams() {
    return {};
  }
}
